import { z } from 'zod';

import {
  companyId,
  companyIdQueryParams,
  DataResponseSchema,
} from './index.js';

export const MetricSchema = z.object({
  integrationId: z.string().nullish(),
  pivotMetricId: z.string().nullish(),
  id: z.string(),
  dataSource: z.string().or(z.null()),
  formula: z.string().or(z.null()),
  useKpi: z.boolean().prefault(false),
  conversionId: z.string().nullish(),
  conversionName: z.string().nullish(),
  fieldDefinitionId: z.string().nullish(),
  metricType: z.string().nullish(),
  isFinancial: z.boolean().nullish(),
  kpiOptimizationDirection: z.enum(['maximize', 'minimize']).nullish(),
  importBeyondEndDate: z.boolean().nullish(),
});

export const DataTypePropertySchema = z.object({
  id: z.string().nullish(),
  maxLength: z.number().or(z.string()).nullish(),
  minLength: z.number().or(z.string()).nullish(),
  maxDigits: z.number().nullish(),
  decimalPlaces: z.number().or(z.string()).nullish(),
  minValue: z.number().or(z.string()).nullish(),
  maxValue: z.number().or(z.string()).nullish(),
  skipValidationProperties: z.array(z.string()).nullish(),
});

const EnumsSchema = z.object({
  dataType: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
    }),
  ),
  group: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
    }),
  ),
  context: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
    }),
  ),
  permission: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
      }),
    )
    .optional(),
  metricDataSource: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        legacyName: z.string().nullish(),
      }),
    )
    .optional(),
  hierarchyGroup: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
      }),
    )
    .optional(),
});

export const DataMetadataEnumsSchema = z.object({
  data: DataResponseSchema(EnumsSchema),
});

export const DataCheckUniquenessSchema = z.object({
  data: z.any().optional(),
});

export const CheckUniquenessFieldDefinitionQuerySchema = z.object({
  name: z.string(),
  companyId: companyIdQueryParams,
});
export const CheckUniquenessHierarchyQuerySchema = z.object({
  name: z.string().optional(),
  companyId: companyIdQueryParams,
});

export const CheckFieldValueUniquenessRequestSchema = z.object({
  excludeId: z.string().or(z.number()).optional(),
  label: z.string().optional(),
  fieldDefinitionId: z.string().optional(),
  globalId: z.string().nullish(),
  companyId: companyIdQueryParams,
});

export const HierarchySchema = z.object({
  id: z.string(),
  urn: z.string().nullish(),
  name: z.string(),
  group: z.string().nullish(),
  companyId: companyId.nullish(),
  createdBy: z.number().nullish(),
  createdAt: z.string().nullish(),
  updatedBy: z.number().nullish(),
  updatedAt: z.string().nullish(),
  createdByFullName: z.string().nullish(),
  updatedByFullName: z.string().nullish(),
  fields: z
    .array(
      z.object({
        id: z.string().optional(),
        name: z.string().optional(),
        label: z.string().nullish(),
      }),
    )
    .nullish(),
});

export const FieldDefinitionSchema = z.object({
  id: z.string(),
  urn: z.string().nullish(),
  group: z.string(),
  dataType: z.string(),
  permission: z
    .enum(['client_readonly', 'client', 'internal'])
    .or(z.null())
    .optional(),
  isSystem: z.boolean().nullish(),
  name: z.string(),
  companyId,
  description: z.union([z.string(), z.null()]),
  createdBy: z.number().nullish(),
  createdAt: z.string().nullish(),
  updatedBy: z.union([z.number(), z.null()]).nullish(),
  updatedAt: z.union([z.string(), z.null()]).nullish(),
  canBeDeleted: z.boolean().nullish(),
  updatedByFullName: z.string().nullish(),
  createdByFullName: z.string().nullish(),
  hierarchies: z.array(HierarchySchema).nullish(),
  metric: MetricSchema.nullish(),
  dataTypeProperty: DataTypePropertySchema.nullish(),
  formula: z.string().nullish(),
  lockValues: z.boolean().nullish(),
  aggregationType: z.enum(['sum', 'none']).nullish(),
});

const FieldDefinitionCountChildSchema = z.object({
  id: z.string(),
  urn: z.string().nullish(),
  group: z.string(),
  dataType: z.string(),
  isSystem: z.boolean().nullish(),
  name: z.string(),
  companyId,
  description: z.union([z.string(), z.null()]),
  createdBy: z.number().nullish(),
  createdAt: z.string().nullish(),
  updatedBy: z.union([z.number(), z.null()]).nullish(),
  updatedAt: z.union([z.string(), z.null()]).nullish(),
  canBeDeleted: z.boolean().nullish(),
  updatedByFullName: z.string().nullish(),
  createdByFullName: z.string().nullish(),
  formula: z.string().nullish(),
  lockValues: z.boolean().nullish(),
  dataTypeProperty: DataTypePropertySchema.nullish(),
  masterDataCount: z.number().nullish(),
  contextMappingsCount: z.number().nullish(),
});

export const DataFieldDefinitionSchema = z.object({
  data: DataResponseSchema(FieldDefinitionSchema),
});

export const FieldDefinitionCountChildDataSchema = z.object({
  data: DataResponseSchema(FieldDefinitionCountChildSchema),
});

export const FieldDefinitionsRequestSchema = z.object({
  companyId: companyIdQueryParams,
  pageNumber: z.number().nullish(),
  itemsPerPage: z.number().nullish(),
  showGlobals: z.boolean().nullish(),
  ordering: z.string().nullish(),
  isMetric: z.boolean().nullish(),
  dataType: z.array(z.string()).nullish(),
  group: z.array(z.string()).nullish(),
  name: z.string().nullish(),
  containsName: z.boolean().nullish(),
  id: z.string().nullish(),
  containsId: z.boolean().nullish(),
  description: z.string().nullish(),
  containsDescription: z.boolean().nullish(),
});

export const FieldDefinitionsCountChildRequestSchema = z.object({
  companyId: companyIdQueryParams,
  pageNumber: z.number().nullish(),
  itemsPerPage: z.number().nullish(),
  showGlobals: z.boolean().nullish(),
  ordering: z.string().nullish(),
  isMetric: z.boolean().nullish(),
  dataType: z.array(z.string()).nullish(),
  group: z.array(z.string()).nullish(),
  name: z.string().nullish(),
  containsName: z.boolean().nullish(),
  id: z.string().nullish(),
  containsId: z.boolean().nullish(),
  description: z.string().nullish(),
  containsDescription: z.boolean().nullish(),
});

const AggregationTypeRequestEnum = z.enum(['sum', 'none']);
export type AggregationTypeRequest = z.infer<typeof AggregationTypeRequestEnum>;

export const CreateFieldDefinitionRequestSchema = z.object({
  group: z.string(),
  dataType: z.string(),
  name: z.string(),
  companyId,
  description: z.string().nullish(),
  permission: z.string().nullish(),
  isSystem: z.boolean().nullish(),
  hierarchies: z.array(z.string()).nullish(),
  metric: MetricSchema.or(z.null()),
  dataTypeProperty: DataTypePropertySchema.nullish(),
  metricUseKpi: z.boolean().or(z.null()),
  metricFormula: z.string().nullish(),
  formula: z.string().nullish(),
  aggregationType: AggregationTypeRequestEnum.nullish(),
});

export const EditFieldDefinitionRequestSchema = z.object({
  group: z.string(),
  description: z.string().nullish(),
  hierarchies: z.array(z.string()).nullish(),
  metric: MetricSchema.or(z.null()),
  dataTypeProperty: DataTypePropertySchema.nullish(),
  metricUseKpi: z.boolean().nullish(),
  metricFormula: z.string().nullish(),
  formula: z.string().nullish(),
  metricKpiOptimizationDirection: z.enum(['maximize', 'minimize']).nullish(),
  aggregationType: AggregationTypeRequestEnum.nullish(),
});

export const FieldMappingRequestSchema = z.object({
  fieldDefinitionId: z.string().optional(),
  label: z.string().nullish(),
  containsLabel: z.string().or(z.boolean()).optional(),
  primaryFieldName: z.string().nullish(),
  containsPrimaryFieldName: z.string().or(z.boolean()).optional(),
  context: z.string().nullish(),
  mappingType: z.string().optional(),
  companyId: companyIdQueryParams,
  pageNumber: z.number().nullish(),
  itemsPerPage: z.number().nullish(),
  ordering: z.string().optional(),
  fieldName: z.string().optional(),
  containsFieldName: z.string().or(z.boolean()).optional(),
  active: z.string().nullish(),
  required: z.string().optional(),
  showGlobals: z.boolean().nullish(),
});

export const HierarchyTreeRequestSchema = z.object({
  hierarchyId: z.string().optional(),
  companyId: companyIdQueryParams,
  showGlobals: z.boolean().nullish(),
});

const ViewSchema = z.object({
  sequence: z.number().nullish(),
  name: z.string().nullish(),
});

export const ConditionalRuleSchema = z.object({
  id: z.string().nullish(),
  properties: z.string().nullish(),
  expression: z.string().nullish(),
});

export const CreateFieldMappingRequestSchema = z.object({
  label: z.string().nullish(),
  context: z.string().nullish(),
  mappingType: z.string().nullish(),
  primaryFieldDefinitionId: z.string().nullish(),
  fieldDefinitionId: z.string(),
  globalId: z.union([z.string(), z.null()]),
  active: z.boolean(),
  companyId,
  required: z.boolean(),
  editable: z.boolean().nullish(),
  conditions: z.array(ConditionalRuleSchema).nullish(),
  initialValue: z.string().or(z.number()).nullish(),
  defaultValue: z.string().or(z.number()).nullish(),
  hierarchyId: z.string().nullish(),
  views: z.array(ViewSchema).nullish(),
  description: z.string().nullish(),
});

export const EditFieldMappingRequestSchema = z.object({
  label: z.string().nullish(),
  active: z.boolean().nullish(),
  required: z.boolean().nullish(),
  editable: z.boolean().nullish(),
  conditions: z.array(ConditionalRuleSchema).nullish(),
  initialValue: z.string().or(z.number()).nullish(),
  defaultValue: z.string().or(z.number()).nullish(),
  views: z.array(ViewSchema).nullish(),
  companyId,
  description: z.string().nullish(),
});
export const FieldMappingsRequestSchema = z.array(
  CreateFieldMappingRequestSchema,
);

export const FieldMappingSchema = z.object({
  id: z.string(),
  fieldDefinitionId: z.string(),
  context: z.string().or(z.null()),
  globalId: z.union([z.string(), z.null()]),
  label: z.union([z.string(), z.null()]),
  fieldName: z.string().nullish(), // fieldDefinitionName
  fieldUrn: z.string().nullish(),
  active: z.boolean(),
  companyId,
  required: z.boolean(),
  editable: z.boolean().nullish(),
  conditions: z.array(ConditionalRuleSchema).nullish(),
  initialValue: z.union([z.string(), z.number(), z.null()]),
  defaultValue: z.union([z.string(), z.number(), z.null()]).optional(),
  createdBy: z.union([z.number(), z.null()]),
  createdAt: z.union([z.string(), z.null()]),
  updatedBy: z.union([z.number(), z.null()]),
  updatedAt: z.union([z.string(), z.null()]),
  updatedByFullName: z.string().nullish(),
  createdByFullName: z.string().nullish(),
  globalCreatedAt: z.union([z.string(), z.null()]).optional(),
  globalUpdatedBy: z.union([z.number(), z.null()]).optional(),
  globalUpdatedAt: z.union([z.string(), z.null()]).optional(),
  globalUpdatedByFullName: z.string().nullish(),
  globalCreatedByFullName: z.string().nullish(),
  primaryFieldDefinitionId: z.string().nullish(),
  primaryFieldName: z.string().nullish(),
  primaryFieldUrn: z.string().nullish(),
  hierarchyId: z.string().nullish(),
  views: z.array(ViewSchema).nullish(),
  isSystem: z.boolean().nullish(),
  globalIsSystem: z.boolean().nullish(),
  description: z.string().nullish(),
  enforcedPrimaryMasterDataId: z.string().nullish(),
});

export const PlanningFormulaFieldMappingSchema = z.object({
  id: z.string(),
  fieldDefinitionId: z.string(),
  context: z.string(),
  label: z.string().nullish(),
  fieldName: z.string(),
  fieldDataType: z.string(),
  active: z.boolean(),
  companyId,
});

export const PlanningFormulaFieldMappingResponseSchema = z.object({
  data: DataResponseSchema(PlanningFormulaFieldMappingSchema),
});

export const PlanningFormulaFieldMappingQuerySchema = z.object({
  companyId: companyIdQueryParams,
  fieldDataTypes: z.string().or(z.array(z.string())).optional(),
});

export const DataFieldMappingSchema = z.object({
  data: DataResponseSchema(FieldMappingSchema),
});

// TODO: remove when will be not used
export const FieldMappingResponseSchema =
  DataResponseSchema(FieldMappingSchema);

export const FieldOptionSchema = z.object({
  id: z.string(),
  name: z.string(),
  dataType: z.string(),
  hierarchies: z.array(z.string()).nullish(),
});

export const FieldOptionsRequestSchema = z.object({
  companyId: companyIdQueryParams,
});

export const FieldDefinitionOptionsResponseSchema = z.object({
  data: DataResponseSchema(FieldOptionSchema),
});

export const FieldValuesOptionSchema = z.object({
  id: z.string(),
  label: z.string(),
});

export const FieldValuesOptionsResponseSchema = z.object({
  data: DataResponseSchema(FieldValuesOptionSchema),
});

export const FieldValuesOptionsRequestSchema = z.object({
  companyId: companyIdQueryParams,
  fieldDefinitionId: z.string().nullish(),
});

export const RelatedMappingsSchema = z.object({
  fieldMappingId: z.string().or(z.number()).nullish(),
  companyId,
  hierarchyId: z.string().nullish(),
  primaryMasterDataLabel: z.string().nullish(),
  primaryMasterDataId: z.string().nullish(),
  masterDataMappingId: z.string().nullish(),
});

const FieldValueSchema = z.object({
  id: z.string(),
  valueId: z.string().nullish(),
  fieldDefinitionId: z.string(),
  companyId: z.string().or(z.number()).or(z.null()),
  globalId: z.string().or(z.null()),
  label: z.string().or(z.null()),
  iconUrl: z.string().nullish(),
  active: z.boolean(),
  createdBy: z.union([z.string(), z.null(), z.number()]).optional(),
  createdAt: z.union([z.string(), z.null(), z.number()]).optional(),
  updatedAt: z.union([z.string(), z.null(), z.number()]).optional(),
  updatedByFullName: z.string().nullish(),
  createdByFullName: z.string().nullish(),
  globalCreatedAt: z.union([z.string(), z.null()]).optional(),
  globalUpdatedAt: z.union([z.string(), z.null()]).optional(),
  globalUpdatedByFullName: z.string().nullish(),
  globalCreatedByFullName: z.string().nullish(),
  hierarchiesMasterDataMappings: z
    .array(RelatedMappingsSchema)
    .or(z.null())
    .optional(),
  relatedFieldsMasterDataMappings: z
    .array(RelatedMappingsSchema)
    .or(z.null())
    .optional(),
  isSystem: z.boolean().nullish(),
});

export const FieldValuesResponseSchema = z.object({
  data: DataResponseSchema(FieldValueSchema),
});

export const CreateFieldValueRequestSchema = z.object({
  label: z.string().nullish(),
  companyId,
  fieldDefinitionId: z.string().nullish(),
  id: z.string().nullish(),
  globalId: z.string().nullish(),
  active: z.boolean().nullish(),
  iconBase64: z.string().nullish(),
  iconFilename: z.string().nullish(),
});

export const CreateFieldValuesMappingRequestSchema = z.array(
  z.object({
    fieldMappingId: z.string().or(z.null()),
    companyId,
    primaryMasterDataId: z.string().nullish(),
    relatedMasterDataId: z.string().nullish(),
  }),
);

export const DeleteFieldValuesMappingRequestSchema = z.array(
  z.object({ masterDataMappingId: z.string().nullish() }),
);

export const EditFieldValueRequestSchema = z.object({
  label: z.string().nullish(),
  fieldDefinitionId: z.string().nullish(),
  active: z.boolean().nullish(),
  iconBase64: z.string().nullish(),
  iconFilename: z.string().nullish(),
  iconUrl: z.string().nullish(),
});

export const FieldValuesRequestSchema = z.object({
  label: z.string().nullish(),
  companyId: companyIdQueryParams,
  fieldDefinitionId: z.string().nullish(),
  ordering: z.string().nullish(),
  pageNumber: z.number().nullish(),
  itemsPerPage: z.number().nullish(),
  active: z.string().nullish(),
});

export const MultiFieldsFieldValuesOptionsResponseSchema = z.object({
  data: DataResponseSchema(
    z.object({
      fieldDefinitionId: z.string(),
      options: z.array(FieldValueSchema),
    }),
  ),
});

export const MultiFieldsFieldValuesOptionsRequestSchema = z.object({
  fieldDefinitionIds: z.array(z.string()).nullish(),
  companyId: companyIdQueryParams,
});

export const HierarchiesResponseSchema = z.object({
  data: DataResponseSchema(HierarchySchema),
});

export const HierarchyAvailableFieldSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export const HierarchyAvailableFieldsResponseSchema = z.object({
  data: DataResponseSchema(HierarchyAvailableFieldSchema),
});

export const HierarchyAvailableFieldsRequestSchema = z.object({
  companyId: companyIdQueryParams,
});

export const CreateHierarchyRequestSchema = z.object({
  name: z.string(),
  group: z.string().nullish(),
  companyId,
});

export const CreateHierarchyWithTreeRequestSchema = z.object({
  name: z.string(),
  group: z.string().nullish(),
  companyId,
  hierarchyNodes: z.array(
    z.object({
      fieldDefinitionId: z.string(),
      label: z.string().nullish(),
    }),
  ),
});

export const HierarchyNodeSchema = z.object({
  id: z.string(),
  name: z.string().nullish(),
  fieldDefinitionId: z.string().optional(),
  fieldName: z.string().nullish(),
  label: z.string().nullish(),
  active: z.boolean().optional(),
  primaryFieldDefinitionId: z.string().nullish(),
  primaryFieldName: z.string().nullish(),
  companyId: companyId.nullish(),
  required: z.boolean().optional(),
  initialValue: z.union([z.string(), z.number(), z.null()]).optional(),
  defaultValue: z.union([z.string(), z.number(), z.null()]).optional(),
});

export const HierarchyWithTreeSchema = HierarchySchema.extend({
  hierarchyNodes: z.array(HierarchyNodeSchema).optional().default([]),
});

export const HierarchiesWithTreeResponseSchema = z.object({
  data: DataResponseSchema(HierarchyWithTreeSchema),
});

export const HierarchiesWithTreeRequestSchema = z.object({
  companyId: companyIdQueryParams,
  name: z.string().optional(),
  group: z.string().optional(),
});

export const ManageHierarchyRequestSchema = z.object({
  hierarchyId: z.string().nullish(),
  fieldDefinitionIds: z.array(z.string()).nullish(),
  action: z.enum(['add', 'remove']),
});

export const ManageHierarchyResponseSchema = z.object({
  data: DataResponseSchema(
    z.object({
      fieldDefinition: FieldDefinitionSchema.optional(),
      errorMessage: z.string().nullish(),
    }),
  )
    .nullable()
    .optional(),
});

export const HierarchiesRequestSchema = z.object({
  companyId: companyIdQueryParams,
});

export const EditHierarchyRequestSchema = z.object({
  group: z.string().nullish(),
});

export const ContextMetadataRequestSchema = z.object({
  companyId: z.number().optional(),
  context: z.string(),
  fieldUrnIds: z.array(z.string()),
  includeDisabled: z.boolean().optional(),
});

export const MdmPlatformSchema = z.object({
  value: z.string(),
  label: z.string(),
  isDisabled: z.boolean(),
  iconUrl: z.string().nullable().optional(),
});

const ContextMetadataFieldValueSchema = z.object({
  label: z.string(),
  type: z.string(),
  defaultValue: z.string().or(z.number()).nullish(),
  isDisabled: z.boolean(),
  dataTypeProperty: DataTypePropertySchema,
  formula: z.string().or(z.null()),
  options: z.array(MdmPlatformSchema),
});

export const ContextMetadataResponseSchema = z.object({
  data: DataResponseSchema(
    z.object({
      contextMetadata: z.record(z.string(), ContextMetadataFieldValueSchema),
    }),
  ),
});

export const DeleteFieldResponseSchema = z.object({
  data: z.object({
    items: z.null(),
    additional: z.any().nullish(),
    itemsPerPage: z.number().nullish(),
    totalItems: z.number().nullish(),
    message: z.string().nullish(),
    ordering: z.string().nullish(),
    pageNumber: z.number().nullish(),
  }),
  error: z.null(),
});

const relatedFieldUrn = z.string();
const primaryFieldUrn = z.string();
const primaryFieldValueUrn = z.string();

export const BuildRelatedFieldsMapRequestSchema = z.object({
  companyId: z.number(),
  context: z.string().optional(),
  relations: z.array(
    z.object({
      relatedFieldDefinitionUrnId: relatedFieldUrn,
      primaryEntitiesUrnMap: z.record(
        primaryFieldUrn,
        z.array(primaryFieldValueUrn),
      ),
    }),
  ),
});

const relatedFieldValueUrn = z.string();
const relatedFieldLabel = z.string();

export const BuildRelatedFieldsMapResponseSchema = z.object({
  data: DataResponseSchema(
    z.object({
      relatedFields: z.record(
        relatedFieldUrn,
        z
          .record(
            primaryFieldUrn,
            z.record(
              primaryFieldValueUrn,
              z.record(relatedFieldValueUrn, relatedFieldLabel),
            ),
          )
          .optional(),
      ),
    }),
  ),
});

export type FieldDefinition = z.infer<typeof FieldDefinitionSchema>;
export type FieldDefinitionCountChild = z.infer<
  typeof FieldDefinitionCountChildSchema
>;
export type Metric = z.infer<typeof MetricSchema>;
export type DataTypePropertySchema = z.infer<typeof DataTypePropertySchema>;
export type FieldMapping = z.infer<typeof FieldMappingSchema>;
export type FieldValue = z.infer<typeof FieldValueSchema>;
export type HierarchyType = z.infer<typeof HierarchySchema>;
export type HierarchyNodeType = z.infer<typeof HierarchyNodeSchema>;
export type HierarchyWithTreeType = z.infer<typeof HierarchyWithTreeSchema>;
export type HierarchyAvailableFieldType = z.infer<
  typeof HierarchyAvailableFieldSchema
>;
export type FieldOptionType = z.infer<typeof FieldOptionSchema>;
export type MdmPlatform = z.infer<typeof MdmPlatformSchema>;
export type ContextMetadataResponse = z.infer<
  typeof ContextMetadataResponseSchema
>;
